import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FaceCanvas from '../components/FaceCanvas';
import RecommendationsPanel from '../components/RecommendationsPanel';
import DownloadOptions from '../components/DownloadOptions';

const ResultsScreen = ({ results, onNewAnalysis }) => {
  const navigate = useNavigate();
  const resultsRef = useRef(null);
  const [activeTab, setActiveTab] = useState('recommendations');
  
  // Handle when user wants to analyze a new image
  const handleNewAnalysis = () => {
    if (onNewAnalysis) {
      onNewAnalysis();
    }
    navigate('/');
  };
  
  // If no results, redirect to home
  if (!results) {
    return null; // Navigate is handled by the App component
  }
  
  const { imagePath, faceShape, description, recommendations, confidence = 85 } = results;
  
  // Get face shape icon with improved SVG paths
  const getFaceShapeIcon = (shape, size = 'w-8 h-8') => {
    switch(shape) {
      case 'oval':
        return (
          <svg className={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <ellipse cx="12" cy="12" rx="6" ry="8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        );
      case 'round':
        return (
          <svg className={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        );
      case 'square':
        return (
          <svg className={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="7" y="7" width="10" height="10" rx="1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        );
      case 'heart':
        return (
          <svg className={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 6C14 3 19 4 19 8C19 11 16 13 12 18C8 13 5 11 5 8C5 4 10 3 12 6Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        );
      case 'diamond':
        return (
          <svg className={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 5L17 12L12 19L7 12L12 5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        );
      case 'oblong':
        return (
          <svg className={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="8" y="5" width="8" height="14" rx="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        );
      default:
        return (
          <svg className={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        );
    }
  };
  
  // Get confidence level indicator with improved design
  const getConfidenceIndicator = (confidence) => {
    let color = 'bg-green-500';
    let textColor = 'text-green-700';
    let message = 'High confidence';
    
    if (confidence < 70) {
      color = 'bg-red-500';
      textColor = 'text-red-700';
      message = 'Low confidence';
    } else if (confidence < 85) {
      color = 'bg-yellow-500';
      textColor = 'text-yellow-700';
      message = 'Medium confidence';
    }
    
    return (
      <div className="flex flex-col sm:flex-row sm:items-center mt-4 bg-white p-3 rounded-lg shadow-sm">
        <div className="text-sm font-medium text-gray-700 mb-2 sm:mb-0 sm:mr-4">Analysis Confidence:</div>
        <div className="flex-1 flex items-center">
          <div className="w-full bg-gray-100 rounded-full h-3">
            <div 
              className={`h-3 rounded-full ${color} transition-all duration-500 ease-in-out`} 
              style={{ width: `${confidence}%` }}
            ></div>
          </div>
          <div className="min-w-[3rem] ml-2 text-sm font-semibold">{confidence}%</div>
        </div>
        <div className={`text-xs font-medium ${textColor} ml-2 mt-1 sm:mt-0`}>{message}</div>
      </div>
    );
  };
  
  // Get all face shape comparisons
  const getAllFaceShapes = () => {
    const shapes = ['oval', 'round', 'square', 'heart', 'diamond', 'oblong'];
    
    return (
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mt-4 sm:mt-6">
        {shapes.map(shape => (
          <div 
            key={shape}
            className={`relative flex flex-col items-center p-2 rounded-lg border-2 transition-all ${
              shape === faceShape 
                ? `border-faceShapes-${shape} bg-faceShapes-${shape} bg-opacity-10` 
                : 'border-gray-200'
            }`}
          >
            {shape === faceShape && (
              <div className="absolute -top-2 -right-2 bg-green-500 rounded-full p-1">
                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            )}
            <div className={`text-faceShapes-${shape}`}>
              {getFaceShapeIcon(shape, 'w-8 h-8 sm:w-12 sm:h-12')}
            </div>
            <span className="text-xs font-medium mt-1 capitalize">{shape}</span>
          </div>
        ))}
      </div>
    );
  };
  
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6">
      <div className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-lg sm:rounded-xl p-4 sm:p-6 mb-6 sm:mb-8 shadow-sm">
        <div className="text-center">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
            Your Analysis Results
          </h1>
          <div className="flex items-center flex-wrap justify-center space-x-0 space-y-2 sm:space-y-0 sm:space-x-3 bg-white px-4 py-3 rounded-full shadow-sm mb-4">
            <span className={`text-faceShapes-${faceShape} flex-shrink-0`}>
              {getFaceShapeIcon(faceShape, 'w-8 h-8 sm:w-10 sm:h-10')}
            </span>
            <p className="text-base sm:text-lg font-medium text-gray-800 ml-2 sm:ml-0">
              You have a <span className={`font-bold text-faceShapes-${faceShape}`}>{faceShape}</span> face shape
            </p>
          </div>
        </div>
        
        {getConfidenceIndicator(confidence)}
      </div>
      
      {/* Tabs for navigation - Responsive design */}
      <div className="flex flex-col sm:flex-row mb-6 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('analysis')}
          className={`py-3 px-4 text-center font-medium flex items-center justify-center ${
            activeTab === 'analysis' 
              ? 'text-primary-600 border-b-2 border-primary-500' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <span>Facial Analysis</span>
        </button>
        <button
          onClick={() => setActiveTab('recommendations')}
          className={`py-3 px-4 text-center font-medium flex items-center justify-center ${
            activeTab === 'recommendations' 
              ? 'text-primary-600 border-b-2 border-primary-500' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <span>Style Recommendations</span>
        </button>
        <button
          onClick={() => setActiveTab('tips')}
          className={`py-3 px-4 text-center font-medium flex items-center justify-center ${
            activeTab === 'tips' 
              ? 'text-primary-600 border-b-2 border-primary-500' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          <span>Style Tips</span>
        </button>
      </div>
      
      {/* Results content that will be captured for download */}
      <div ref={resultsRef} className="space-y-4 sm:space-y-8 bg-white p-4 sm:p-6 rounded-lg shadow-md">
        {activeTab === 'analysis' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-gray-50 p-4 rounded-lg shadow-sm">
              <FaceCanvas 
                imageUrl={imagePath} 
                faceShape={faceShape} 
              />
              {getAllFaceShapes()}
            </div>
            
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-100">
              <div className="flex flex-wrap items-center mb-4">
                <span className={`text-faceShapes-${faceShape} mr-3 p-2 bg-gray-50 rounded-full mb-2 sm:mb-0`}>
                  {getFaceShapeIcon(faceShape, 'w-8 h-8 sm:w-10 sm:h-10')}
                </span>
                <h3 className="text-lg sm:text-xl font-semibold">About Your {faceShape.charAt(0).toUpperCase() + faceShape.slice(1)} Face Shape</h3>
              </div>
              
              <p className="text-gray-700 mb-6 leading-relaxed text-sm sm:text-base">{description}</p>
              
              <div className="border-t border-gray-100 pt-4">
                <h4 className="font-medium mb-3 flex items-center text-gray-800 text-sm sm:text-base">
                  <svg className="w-5 h-5 mr-2 text-primary-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                  Key Characteristics
                </h4>
                <ul className="space-y-2">
                  {getFaceShapeCharacteristics(faceShape).map((characteristic, index) => (
                    <li key={index} className="flex items-start bg-gray-50 p-2 rounded-md">
                      <svg className="h-5 w-5 text-primary-500 mt-0.5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-gray-700 text-sm sm:text-base">{characteristic}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="border-t border-gray-100 pt-4 mt-6">
                <h4 className="font-medium mb-3 flex items-center text-gray-800 text-sm sm:text-base">
                  <svg className="w-5 h-5 mr-2 text-primary-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                  </svg>
                  Face Shape Balance
                </h4>
                <div className="h-4 w-full bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    className={`h-full bg-faceShapes-${faceShape} transition-all duration-500 ease-in-out`} 
                    style={{ width: getFaceShapeBalanceWidth(faceShape) }}
                  ></div>
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1 px-1">
                  <span>Narrow</span>
                  <span>Balanced</span>
                  <span>Wide</span>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'recommendations' && (
          <RecommendationsPanel 
            faceShape={faceShape}
            recommendations={recommendations}
          />
        )}
        
        {activeTab === 'tips' && (
          <div className="bg-white rounded-lg">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-center text-gray-800 flex flex-wrap items-center justify-center">
              <svg className="w-6 h-6 text-primary-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              <span>Style Tips for {faceShape.charAt(0).toUpperCase() + faceShape.slice(1)} Face Shape</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div className="bg-green-50 p-3 sm:p-5 rounded-lg border border-green-100">
                <h3 className="flex items-center text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-green-800">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>What Works Well</span>
                </h3>
                <ul className="space-y-2 sm:space-y-3">
                  {getStyleDos(faceShape).map((tip, index) => (
                    <li key={index} className="flex items-start bg-white p-2 sm:p-3 rounded-md shadow-sm">
                      <svg className="h-5 w-5 text-green-600 mt-0.5 mr-2 sm:mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-700 text-sm sm:text-base">{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-red-50 p-3 sm:p-5 rounded-lg border border-red-100">
                <h3 className="flex items-center text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-red-800">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>What to Avoid</span>
                </h3>
                <ul className="space-y-2 sm:space-y-3">
                  {getStyleDonts(faceShape).map((tip, index) => (
                    <li key={index} className="flex items-start bg-white p-2 sm:p-3 rounded-md shadow-sm">
                      <svg className="h-5 w-5 text-red-600 mt-0.5 mr-2 sm:mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <span className="text-gray-700 text-sm sm:text-base">{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Download options */}
      <div className="mt-6 sm:mt-8 bg-gray-50 p-3 sm:p-5 rounded-lg shadow-md">
        <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3 flex items-center">
          <svg className="w-5 h-5 mr-2 text-primary-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Save Your Results
        </h3>
        <p className="text-gray-600 mb-3 sm:mb-4 text-sm sm:text-base">Download your analysis results to reference later or share with others.</p>
        
        <DownloadOptions 
          resultsRef={resultsRef}
          faceShape={faceShape}
          imageUrl={imagePath}
          recommendations={recommendations}
        />
      </div>
      
      {/* Action buttons */}
      <div className="mt-6 sm:mt-8 text-center">
        <button 
          onClick={handleNewAnalysis}
          className="btn-primary text-base sm:text-lg px-6 sm:px-8 py-2 sm:py-3 shadow-md hover:shadow-lg transition duration-300 flex items-center mx-auto"
        >
          <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Analyze Another Photo
        </button>
      </div>
      
      {/* Disclaimer */}
      <div className="mt-6 sm:mt-8 text-xs sm:text-sm text-center text-gray-500 bg-gray-50 p-3 sm:p-4 rounded-lg">
        <p className="flex flex-wrap items-center justify-center">
          <svg className="w-4 h-4 mr-1 sm:mr-2 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>These recommendations are based on traditional style guidance for different face shapes.
          Remember that personal preference and individual style are always the most important factors!</span>
        </p>
      </div>
    </div>
  );
};

// Helper function: Get face shape characteristics
const getFaceShapeCharacteristics = (faceShape) => {
  const characteristics = {
    oval: [
      "Balanced proportions with a gently rounded jawline",
      "Forehead is slightly wider than the chin",
      "Face length is about one and a half times the width",
      "Cheekbones are the widest part of the face"
    ],
    round: [
      "Width and length of the face have similar measurements",
      "Full cheeks with a rounded jawline and chin",
      "Soft features with few angles",
      "Face shape creates a circular appearance"
    ],
    square: [
      "Strong, angular jawline with minimal curves",
      "Forehead, cheekbones, and jawline are similar in width",
      "Defined, sharp corners at the jaw",
      "Face has straight lines and a structured appearance"
    ],
    heart: [
      "Wider forehead and cheekbones with a narrow jawline",
      "Chin comes to a distinct point",
      "Face tapers from top to bottom",
      "May have a widow's peak hairline"
    ],
    diamond: [
      "Narrow forehead and jawline with wide cheekbones",
      "Cheekbones are the widest part of the face",
      "Pointed chin similar to heart shape",
      "Face has angular features with defined cheekbones"
    ],
    oblong: [
      "Face length is greater than its width",
      "Forehead, cheeks, and jawline have similar widths",
      "Longer, straight cheek line",
      "Chin is slightly rounded or squared off"
    ]
  };
  
  return characteristics[faceShape] || [];
};

// Helper function: Get face shape balance width percentage
const getFaceShapeBalanceWidth = (faceShape) => {
  const balances = {
    oval: '50%',    // Perfectly balanced
    round: '75%',   // Wider
    square: '70%',  // Wider
    heart: '60%',   // Slightly wider at top
    diamond: '50%', // Balanced (wide in middle, narrow at top/bottom)
    oblong: '30%'   // Narrower
  };
  
  return balances[faceShape] || '50%';
};

// Helper function: Get style dos
const getStyleDos = (faceShape) => {
  const dos = {
    oval: [
      "Experiment with most styles as oval faces are versatile",
      "Try angular glasses to add definition",
      "Consider side-parted hairstyles for a classic look",
      "Wear V-neck or scoop neck tops to elongate the face"
    ],
    round: [
      "Choose angular glasses to add definition",
      "Opt for hairstyles with height and volume at the crown",
      "Try asymmetrical or side-parted styles",
      "Wear vertical patterns and V-necks to create length"
    ],
    square: [
      "Select round or oval glasses to soften angles",
      "Try hairstyles with soft layers and waves",
      "Choose side-swept bangs to add softness",
      "Wear round or scoop necklines to balance angular features"
    ],
    heart: [
      "Choose frames that are wider at the bottom",
      "Try hairstyles with volume at the jawline",
      "Wear chokers or short necklaces to draw attention to the neck",
      "Opt for tops with details at the lower part"
    ],
    diamond: [
      "Choose glasses with detailing or distinctive brow lines",
      "Try hairstyles with volume at the forehead and jawline",
      "Wear earrings to draw attention to the middle of the face",
      "Choose boat necks or wider necklines to balance cheekbones"
    ],
    oblong: [
      "Select round or square glasses with wide frames",
      "Try hairstyles with bangs to shorten the face",
      "Wear horizontal patterns to widen the face",
      "Choose turtlenecks or chokers to break up length"
    ]
  };
  
  return dos[faceShape] || [];
};

// Helper function: Get style don'ts
// Helper function: Get style don'ts
const getStyleDonts = (faceShape) => {
  const donts = {
    oval: [
      "Avoid hairstyles that cover up too much of your face",
      "Don't select glasses that are too oversized",
      "Avoid heavy bangs that can disrupt your balanced proportions",
      "Don't wear accessories that overwhelm your features"
    ],
    round: [
      "Avoid round glasses that emphasize facial roundness",
      "Don't choose hairstyles that add width at the cheeks",
      "Avoid blunt bangs that make the face appear fuller",
      "Don't wear turtlenecks that can make your face appear shorter"
    ],
    square: [
      "Avoid square or rectangular glasses that accentuate angles",
      "Don't choose hairstyles with harsh lines or angles",
      "Avoid center parts that emphasize symmetry",
      "Don't wear high necklines that emphasize a strong jawline"
    ],
    heart: [
      "Avoid heavy or bold glasses on the upper part of the frame",
      "Don't choose hairstyles with too much volume on top",
      "Avoid chunky necklaces that draw attention to a narrow chin",
      "Don't wear boat necks that emphasize shoulder width"
    ],
    diamond: [
      "Avoid narrow glasses that emphasize face width at cheekbones",
      "Don't choose hairstyles that are flat at the crown",
      "Avoid hairstyles that are too full at the cheeks",
      "Don't wear chunky earrings that compete with cheekbones"
    ],
    oblong: [
      "Avoid small, narrow glasses that elongate the face",
      "Don't choose hairstyles with too much height on top",
      "Avoid center parts that emphasize face length",
      "Don't wear V-necks that visually elongate the face"
    ]
  };
  
  return donts[faceShape] || [];
};

export default ResultsScreen;